import React, {Dispatch, SetStateAction, useState} from 'react';
import {IconButton, Tooltip} from "@material-tailwind/react";
import {showToast} from "../CustomToast";
import {useTranslation} from "react-i18next";
import {Delete, Description} from "@mui/icons-material";
import DescriptionFileInput from "./DescriptionFileInput";
import {DescriptionFileInterface} from "../../../services/interface/DescriptionFileInterface";

export interface FileUploadModalProps {
    files: File[];
    setFiles: Dispatch<SetStateAction<File[]>>;
    descriptions: DescriptionFileInterface[];
    setDescriptions: Dispatch<SetStateAction<DescriptionFileInterface[]>>;
    accept: string
    acceptDragAndDrop: string[]
}

const FileUploadModal = ({
                             files, setFiles, descriptions, setDescriptions, accept, acceptDragAndDrop
}: FileUploadModalProps) => {
    const { t } = useTranslation("global") as { t: (key: string) => string }
    const maxFileSize = 10 * 1024 * 1024; // 10 MB
    const [isDraggingOver, setIsDraggingOver] = useState(false);

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDraggingOver(false);

        const droppedFiles = Array.from(e.dataTransfer.files);

        const allowedTypes = acceptDragAndDrop;
        const filteredFiles = droppedFiles.filter(file => allowedTypes.includes(file.type) && file.size <= maxFileSize);
        if (filteredFiles.length < droppedFiles.length) {
            showToast(t('minimun-size-file'), 'info');
        }
        setFiles((prevFiles) => [...prevFiles, ...filteredFiles]);
    };

    const handleDragEnter = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDraggingOver(true);
    };

    const handleDragLeave = () => {
        setIsDraggingOver(false);
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
    };

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputFiles = Array.from(e.target.files || []);
        const filteredFiles = inputFiles.filter((file) => file.size <= maxFileSize);
        if (filteredFiles.length < inputFiles.length) {
            showToast(t('minimun-size-file'), 'info');
        }
        setFiles((prevFiles) => [...prevFiles, ...filteredFiles]);
    };

    const handleDeleteFile = (file: File) => {
        setFiles((prevFiles) => prevFiles.filter((f) => f !== file));
    };

    return (
        <main className="container w-full mt-4">
            <article
                aria-label="File Upload Modal"
                className={`relative h-full flex flex-col bg-white`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDragEnter={handleDragEnter}
            >
                <section className="h-full overflow-auto py-1 px-3 w-full flex flex-col">
                    <header
                        className="border-dashed border-2 border-gray-400 py-12 flex flex-col justify-center items-center">
                        <input
                            id="hidden-input"
                            type="file"
                            multiple
                            className="hidden"
                            accept={accept}
                            onChange={handleFileInputChange}
                        />
                        <label
                            htmlFor="hidden-input"
                            className="mt-2 px-3 py-1 bg-gray-200 hover:bg-gray-300 focus:shadow-outline focus:outline-none cursor-pointer rounded-[9px]"
                        >
                            {t('upload-file')}
                        </label>
                        <div
                            className={`mt-2 w-full h-full pointer-events-none flex flex-col items-center justify-center rounded-md ${isDraggingOver ? 'bg-gray-200' : ''}`}
                        >
                            <i>
                                <svg
                                    className="fill-current w-12 h-12 text-blue-700"
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        d="M19.479 10.092c-.212-3.951-3.473-7.092-7.479-7.092-4.005 0-7.267 3.141-7.479 7.092-2.57.463-4.521 2.706-4.521 5.408 0 3.037 2.463 5.5 5.5 5.5h13c3.037 0 5.5-2.463 5.5-5.5 0-2.702-1.951-4.945-4.521-5.408zm-7.479-1.092l4 4h-3v4h-2v-4h-3l4-4z"/>
                                </svg>
                            </i>
                            <p className="text-lg text-blue-700">{t('drop-upload-file')}</p>
                        </div>
                    </header>

                    <h1 className="pt-8 pb-3 font-semibold sm:text-lg text-gray-900">{t('list-file')}</h1>

                    {/* image gallery */}
                    <ul className="flex flex-1 flex-wrap -m-1">
                        {files.length === 0 && (
                            <li
                                id="empty"
                                className={`h-full w-full text-center flex flex-col justify-center items-center ${isDraggingOver ? 'hidden' : ''}`}
                            >
                                <img
                                    className="mx-auto w-32"
                                    src="https://user-images.githubusercontent.com/507615/54591670-ac0a0180-4a65-11e9-846c-e55ffce0fe7b.png"
                                    alt="no data"
                                />
                                <span className="text-small text-gray-500">{t('empty-select-files')}</span>
                            </li>
                        )}

                        {files.map((file, index) => (
                            <li
                                key={index}
                                className="block p-1 w-1/2 sm:w-1/3 md:w-1/4 lg:w-1/6 xl:w-1/10 h-[160px] md:h-[140px]"
                            >
                                <article
                                    tabIndex={0}
                                    className="group w-full h-full rounded-md focus:outline-none focus:shadow-outline relative bg-headerMenus cursor-pointer shadow-sm"
                                >
                                    <img
                                        alt="upload preview"
                                        className="img-preview hidden w-full h-full sticky object-cover rounded-md bg-fixed"
                                    />

                                    <div
                                        className="flex flex-col rounded-md text-xs break-words w-full h-full z-20 absolute top-0 py-1 px-1">
                                        <h6 className="flex-1 group-hover:text-white mt-1 mx-2 truncate">{file.name}</h6>
                                        <DescriptionFileInput {...{index, descriptions, setDescriptions}}/>
                                        <div className="grid sm:grid-cols-2">
                                            <div className={'flex items-center h-full'}>
                                                <Description className={'min-w-[20px] h-[20px] ml-2'}/>
                                                <p className="p-1 size text-xs text-white">
                                                    {file.size > 1024
                                                        ? file.size > 1048576
                                                            ? Math.round(file.size / 1048576) + 'mb'
                                                            : Math.round(file.size / 1024) + 'kb'
                                                        : file.size + 'b'}
                                                </p>
                                            </div>
                                            <div className={'text-right'}>
                                                <Tooltip className={"bg-primary"} content="Eliminar">
                                                    <IconButton placeholder="icon-button"  variant="text" color="red"
                                                                onClick={() => handleDeleteFile(file)}>
                                                        <Delete className={'w-[20px]'}/>
                                                    </IconButton>
                                                </Tooltip>
                                            </div>
                                        </div>
                                    </div>
                                </article>
                            </li>
                        ))}
                    </ul>
                </section>
            </article>
        </main>
    );
};

export default FileUploadModal;
